/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import urlJoin from 'proper-url-join';

import {
  CreateTestResponse,
  DeleteTestResponse,
  TestResultsResponse,
  TestSettingsResponse
} from '../../../../ts/insight-actions';
import { endpoints } from '../../util/config';
import { saveAs } from '../../util/download';

const testDataEndpoint = (assistantId: string, environmentId: string): string =>
  `${endpoints.store}/v2/assistants/${assistantId}/environments/${environmentId}/tests`;

const deleteTestEndpoint = (
  assistantId: string,
  environmentId: string,
  testId
): string =>
  `${endpoints.store}/v2/assistants/${assistantId}/environments/${environmentId}/tests/${testId}`;

const reportsEndpoint = (assistantId: string): string =>
  urlJoin(
    endpoints.store,
    'v2',
    'tooling',
    'assistants',
    assistantId,
    'reports'
  );

export const getTestSettings = async (
  assistantId: string,
  environmentId: string,
  testId: string
): Promise<TestSettingsResponse> => {
  try {
    const response = await axios.get(
      testDataEndpoint(assistantId, environmentId) + `/${testId}`
    );

    return response?.data;
  } catch (error) {
    throw error;
  }
};
export const postTestData = async (
  assistantId: string,
  environmentId: string,
  data: Array<{ input: string }>
): Promise<CreateTestResponse> => {
  try {
    const response = await axios.post(
      testDataEndpoint(assistantId, environmentId),
      {
        inputs: data
      }
    );
    return response?.data;
  } catch (error) {
    throw error;
  }
};

export const deleteTest = async (
  assistantId: string,
  environmentId: string,
  testId: string
): Promise<DeleteTestResponse> => {
  try {
    const response = await axios.delete(
      deleteTestEndpoint(assistantId, environmentId, testId)
    );

    return response?.data;
  } catch (error) {
    throw error;
  }
};

export const getTestResults = async (
  language: string,
  assistantId: string,
  testId: string,
  recognitionTypes: string[]
): Promise<TestResultsResponse> => {
  const params = {
    type: 'reviewTestResults',
    options: {
      language: language,
      assistant_id: assistantId,
      test_id: testId,
      version: 'v2',
      sample_size: 250,
      resolution_method: recognitionTypes
    }
  };

  return await axios
    .post(reportsEndpoint(assistantId), params)
    .then(response => {
      return response.data;
    });
};

const escapeResult = value => `"${value.replace(/"/g, '""')}"`;

export const exportTestResults = async (
  language: string,
  assistantId: string,
  testId: string
) => {
  const params = {
    type: 'exportTestResults',
    options: {
      language: language,
      assistant_id: assistantId,
      test_id: testId,
      version: 'v2'
    }
  };

  const response = await axios.post(reportsEndpoint(assistantId), params);

  //Format data
  const reports = response.data.reports;
  const csvResults = [
    [
      'conversation_id',
      'timestamp',
      'is_idk_response',
      'input',
      'response',
      'response_confidence',
      'extractiveness',
      'retrieval_confidence',
      'recognition_type',
      'original_response',
      'action_id',
      'action_title'
    ],
    ...reports.results.map(item => [
      item.conversation_id,
      item.timestamp,
      item.is_idk_response,
      item.input ? escapeResult(item.input) : '', // done to escape commas in input
      item.response ? escapeResult(item.response) : '', // done to escape commas, quotes in response
      item.response_confidence,
      item.extractiveness,
      item.retrieval_confidence,
      item.recognition_type,
      item.original_response ? escapeResult(item.original_response) : '', // done to escape commas in response
      item.action_id,
      item.action_title
    ])
  ]
    .map(e => e.join(','))
    .join('\n');
  // Place data in file format
  const filename = `${testId}.csv`;
  const file = new Blob([csvResults], { type: 'text/csv; charset=UTF-8' });
  // Export
  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(file, filename);
  } else {
    saveAs(URL.createObjectURL(file), filename);
  }
};
